import apiService from './api';

export interface Ticker {
  _id: string;
  symbol: string;
  name: string;
  assetType: string;
  exchange: string;
  sector?: string;
  description?: string;
  isActive: boolean;
  isTradable: boolean;
  includeInAnalysis: boolean;
  isWatchlist: boolean;
  analysisPriority: number;
  lastPrice?: {
    lastPrice: number;
    change: number;
    changePercent: number;
    volume: number;
    timestamp: Date;
  };
  fundamentalData?: {
    marketCap?: number;
    peRatio?: number;
    dividendYield?: number;
  };
  createdAt: Date;
  updatedAt: Date;
}

export interface TickerFilters {
  search?: string;
  assetType?: string;
  exchange?: string;
  sector?: string;
  isActive?: boolean;
  isTradable?: boolean;
  includeInAnalysis?: boolean;
  isWatchlist?: boolean;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface TickerResponse {
  tickers: Ticker[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface TickerSummary {
  total: number;
  byAssetType: Record<string, number>;
  byExchange: Record<string, number>;
  bySector: Record<string, number>;
  active: number;
  watchlist: number;
  analysis: number;
}

export interface CreateTickerData {
  symbol: string;
  name?: string;
  assetType?: string;
  exchange?: string;
  description?: string;
  sector?: string;
  industry?: string;
  marketCapCategory?: string;
  country?: string;
  currency?: string;
  tags?: string[];
  keywords?: string[];
  includeInAnalysis?: boolean;
  analysisPriority?: number;
  isWatchlist?: boolean;
  isActive?: boolean;
  isTradable?: boolean;
  lastPrice?: {
    lastPrice: number;
    change: number;
    changePercent: number;
    volume: number;
    timestamp: Date;
  };
  fundamentalData?: {
    marketCap?: number;
    peRatio?: number;
    dividendYield?: number;
  };
}

export interface UpdateTickerData {
  name?: string;
  description?: string;
  sector?: string;
  industry?: string;
  tags?: string[];
  keywords?: string[];
  includeInAnalysis?: boolean;
  analysisPriority?: number;
  isWatchlist?: boolean;
  isActive?: boolean;
  isTradable?: boolean;
  lastPrice?: {
    lastPrice: number;
    change: number;
    changePercent: number;
    volume: number;
    timestamp: Date;
  };
  fundamentalData?: {
    marketCap?: number;
    peRatio?: number;
    dividendYield?: number;
  };
}

export interface BulkOperationResult {
  created?: number;
  updated?: number;
  deleted?: number;
  failed: number;
  errors: Array<{
    symbol: string;
    error: string;
  }>;
}

class TickerService {
  // Get all tickers with filtering and pagination
  async getTickers(filters: TickerFilters = {}): Promise<TickerResponse> {
    return await apiService.getTickers(filters);
  }

  // Get ticker by ID
  async getTickerById(id: string): Promise<Ticker> {
    return await apiService.getTickerById(id);
  }

  // Get ticker by symbol
  async getTickerBySymbol(symbol: string): Promise<Ticker> {
    return await apiService.getTickerBySymbol(symbol);
  }

  // Search tickers
  async searchTickers(query: string, limit?: number): Promise<Ticker[]> {
    return await apiService.searchTickers(query, limit);
  }

  // Get ticker summary
  async getTickerSummary(): Promise<TickerSummary> {
    return await apiService.getTickerSummary();
  }

  // Get tickers for analysis
  async getTickersForAnalysis(limit?: number): Promise<Ticker[]> {
    return await apiService.getTickersForAnalysis(limit);
  }

  // Get watchlist tickers
  async getWatchlistTickers(): Promise<Ticker[]> {
    return await apiService.getWatchlistTickers();
  }

  // Create ticker
  async createTicker(data: CreateTickerData): Promise<Ticker> {
    return await apiService.createTicker(data);
  }

  // Update ticker
  async updateTicker(id: string, data: UpdateTickerData): Promise<Ticker> {
    return await apiService.updateTicker(id, data);
  }

  // Delete ticker
  async deleteTicker(id: string): Promise<void> {
    await apiService.deleteTicker(id);
  }

  // Bulk create tickers
  async bulkCreateTickers(
    tickers: CreateTickerData[],
  ): Promise<BulkOperationResult> {
    return await apiService.bulkCreateTickers(tickers);
  }

  // Bulk update tickers
  async bulkUpdateTickers(
    updates: Array<{ id: string; data: UpdateTickerData }>,
  ): Promise<BulkOperationResult> {
    return await apiService.bulkUpdateTickers(updates);
  }

  // Bulk delete tickers
  async bulkDeleteTickers(ids: string[]): Promise<BulkOperationResult> {
    return await apiService.bulkDeleteTickers(ids);
  }

  // Update ticker price
  async updateTickerPrice(symbol: string, priceData: any): Promise<Ticker> {
    return await apiService.updateTickerPrice(symbol, priceData);
  }

  // Toggle watchlist status
  async toggleWatchlist(symbol: string): Promise<Ticker> {
    return await apiService.toggleWatchlist(symbol);
  }

  // Get tickers by asset type
  async getTickersByAssetType(assetType: string): Promise<Ticker[]> {
    return await apiService.getTickersByAssetType(assetType);
  }

  // Get tickers by exchange
  async getTickersByExchange(exchange: string): Promise<Ticker[]> {
    return await apiService.getTickersByExchange(exchange);
  }

  // Get tickers by sector
  async getTickersBySector(sector: string): Promise<Ticker[]> {
    return await apiService.getTickersBySector(sector);
  }

  // Development endpoints
  async seedTickers(): Promise<{ message: string }> {
    return await apiService.seedTickers();
  }

  async clearTickers(): Promise<{ message: string }> {
    return await apiService.clearTickers();
  }

  async getSeedingStats(): Promise<TickerSummary> {
    return await apiService.getSeedingStats();
  }
}

export const tickerService = new TickerService();
export default tickerService;
